// Generic copy-to-clipboard controller.
//
// Copies the text of the `contentToCopy` target (an input's value, or any other
// element's text content) to the clipboard. When the optional `button` target is
// present, the button's content is swapped for a success label after a
// successful copy, then reverted to its original content after a short delay so
// the user gets visible confirmation.
//
// Sample usage:
//
//   <div data-controller="copy-to-clipboard"
//        data-copy-to-clipboard-success-content-value="Copied!"
//        data-copy-to-clipboard-success-duration-value="3000">
//     <span hidden data-copy-to-clipboard-target="contentToCopy">Text to copy</span>
//     <button type="button" aria-live="polite"
//             data-copy-to-clipboard-target="button"
//             data-action="click->copy-to-clipboard#copyToClipboard">Copy</button>
//   </div>
//
// Both values are optional; feedback defaults to "Copied ✓" shown for 2 seconds.
// (This project runs Stimulus 2, which lacks declarative value defaults, so the
// fallbacks are applied inline where each value is read.)
import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [ "contentToCopy", "button" ]
  static values = { successContent: String, successDuration: Number }

  // Remember the button's original markup so the success label can revert to it.
  connect() {
    if (this.hasButtonTarget) {
      this.originalButtonContent = this.buttonTarget.innerHTML
    }
  }

  disconnect() {
    clearTimeout(this.revertTimeout)
  }

  // Writes the target's text to the clipboard, then shows the success feedback.
  // Clipboard access requires a secure context (HTTPS or localhost); if the
  // browser rejects the write, the button is left untouched so we never claim a
  // copy happened when it didn't.
  copyToClipboard() {
    if (this.hasContentToCopyTarget) {
      const source = this.contentToCopyTarget
      // Inputs and textareas expose their text via `value`; every other element
      // via `textContent` (not `innerHTML`, which would copy escaped entities).
      const text = source.value || source.textContent

      navigator.clipboard.writeText(text).then(() => this.showSuccessFeedback())
    }
  }

  // Swaps the button content for the success label, reverting after the
  // configured duration. Repeated clicks restart the revert timer instead of
  // stacking timeouts (which could revert a fresh label too early).
  showSuccessFeedback() {
    if (this.hasButtonTarget) {
      clearTimeout(this.revertTimeout)
      this.buttonTarget.innerHTML = this.successContentValue || "Copied ✓"

      this.revertTimeout = setTimeout(() => {
        this.buttonTarget.innerHTML = this.originalButtonContent
      }, this.successDurationValue || 2000)
    }
  }
}
